import { InvoiceStatusBadge } from './InvoiceStatusBadge';
import { formatAbsoluteDateTime, formatCurrency, formatRelativeDate } from '../../lib/formatters';
import type { ExtranetInvoiceOutput } from '../../types/extranet-api.types';
import { IconDownload, IconInbox } from '../incidents/icons';

interface InvoicesTableProps {
  items: ExtranetInvoiceOutput[];
  total: number;
  isLoading: boolean;
  isFetching: boolean;
  selectedId: string | null;
  onRowClick: (item: ExtranetInvoiceOutput) => void;
  /** Si non fourni (perm `invoices.download` à false), la colonne PDF est masquée. */
  onDownloadPdf?: (item: ExtranetInvoiceOutput) => void;
}

const SKELETON_ROWS = 10;

const MONTHS = ['janv.', 'févr.', 'mars', 'avril', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

/**
 * Le back livre `periodFrom` / `periodTo` en YYYY-MM-DD (Yohan 2026-06-17).
 * On les rendu en libellé condensé : "janv. 2026" / "janv.–mars 2026".
 */
const formatPeriodFromInvoice = (invoice: ExtranetInvoiceOutput): string => {
  const from = new Date(invoice.periodFrom);
  const to = new Date(invoice.periodTo);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return '—';
  const fMonth = from.getUTCMonth();
  const tMonth = to.getUTCMonth();
  const fYear = from.getUTCFullYear();
  const tYear = to.getUTCFullYear();
  const sameYear = fYear === tYear;
  const sameMonth = sameYear && fMonth === tMonth;
  if (sameMonth) return `${MONTHS[fMonth]} ${fYear}`;
  if (sameYear) return `${MONTHS[fMonth]}–${MONTHS[tMonth]} ${fYear}`;
  return `${MONTHS[fMonth]} ${fYear} – ${MONTHS[tMonth]} ${tYear}`;
};

/** Conversion centimes back → euros pour `formatCurrency`. */
const toEuros = (cents: number): number => cents / 100;

export const InvoicesTable = ({
  items,
  total,
  isLoading,
  isFetching,
  selectedId,
  onRowClick,
  onDownloadPdf,
}: InvoicesTableProps) => {
  const colCount = onDownloadPdf ? 9 : 8;
  return (
    <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50/40">
              <Th>N° Facture</Th>
              <Th>Émise</Th>
              <Th>Entité</Th>
              <Th>Contrat</Th>
              <Th>Période</Th>
              <Th align="right">Montant HT</Th>
              <Th align="right">Montant TTC</Th>
              <Th>Statut</Th>
              {onDownloadPdf ? <Th align="right">Actions</Th> : null}
            </tr>
          </thead>
          <tbody className={isFetching && !isLoading ? 'opacity-60 transition-opacity' : ''}>
            {isLoading ? (
              Array.from({ length: SKELETON_ROWS }).map((_, i) => <SkeletonRow key={i} colCount={colCount} />)
            ) : items.length === 0 ? (
              <EmptyRow colCount={colCount} />
            ) : (
              items.map(item => (
                <tr
                  key={item.id}
                  onClick={() => onRowClick(item)}
                  className={[
                    'border-b border-zinc-100 last:border-b-0 cursor-pointer transition-colors duration-100',
                    item.id === selectedId ? 'bg-zinc-50' : 'hover:bg-zinc-50/60',
                  ].join(' ')}
                >
                  <td className="px-4 py-3 font-mono text-sm tabular-nums text-zinc-900 whitespace-nowrap">
                    {item.invoiceNumber}
                  </td>
                  <td
                    className="px-4 py-3 font-mono text-sm tabular-nums text-zinc-500 whitespace-nowrap"
                    title={formatAbsoluteDateTime(item.issuedAt)}
                  >
                    {formatRelativeDate(item.issuedAt)}
                  </td>
                  <td className="px-4 py-3 text-zinc-700">
                    <span className="block max-w-[160px] truncate" title={item.entityName}>
                      {item.entityName}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-sm tabular-nums text-zinc-500 whitespace-nowrap">
                    {item.contractRef || '—'}
                  </td>
                  <td className="px-4 py-3 text-zinc-700 whitespace-nowrap">{formatPeriodFromInvoice(item)}</td>
                  <td className="px-4 py-3 font-mono text-sm tabular-nums text-zinc-900 text-right whitespace-nowrap">
                    {formatCurrency(toEuros(item.amountExcludingTax))}
                  </td>
                  <td className="px-4 py-3 font-mono text-sm tabular-nums text-zinc-900 text-right whitespace-nowrap">
                    {formatCurrency(toEuros(item.amountIncludingTax))}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <InvoiceStatusBadge displayStatus={item.status === 'validated' ? 'validated' : 'pending'} />
                  </td>
                  {onDownloadPdf ? (
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          onDownloadPdf(item);
                        }}
                        aria-label="Télécharger la facture en PDF"
                        title="Télécharger PDF"
                        className="inline-flex items-center gap-1 h-7 px-2 text-xs text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded transition-colors"
                      >
                        <IconDownload width={12} height={12} />
                        <span>PDF</span>
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {!isLoading && items.length > 0 ? (
        <div className="px-4 py-2.5 text-xs text-zinc-500 border-t border-zinc-100 flex items-center justify-between">
          <span>
            <span className="font-mono tabular-nums text-zinc-700">{items.length}</span> affichée
            {items.length > 1 ? 's' : ''} sur <span className="font-mono tabular-nums text-zinc-700">{total}</span>
          </span>
          <span className="text-zinc-400">Cliquez sur une ligne pour le listing des courses</span>
        </div>
      ) : null}
    </div>
  );
};

const Th = ({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) => (
  <th
    className={`px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-zinc-500 ${align === 'right' ? 'text-right' : 'text-left'}`}
  >
    {children}
  </th>
);

const SKELETON_WIDTHS = [60, 50, 70, 65, 55, 45, 50, 45, 30];

const SkeletonRow = ({ colCount }: { colCount: number }) => (
  <tr className="border-b border-zinc-100 last:border-b-0">
    {Array.from({ length: colCount }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <div className="h-3 rounded-sm skeleton-pulse" style={{ width: `${SKELETON_WIDTHS[i] ?? 50}%` }} />
      </td>
    ))}
  </tr>
);

const EmptyRow = ({ colCount }: { colCount: number }) => (
  <tr>
    <td colSpan={colCount} className="px-4 py-16">
      <div className="flex flex-col items-center justify-center gap-3 text-center">
        <IconInbox width={28} height={28} className="text-zinc-300" />
        <div>
          <p className="text-sm text-zinc-900">Aucune facture sur cette période.</p>
          <p className="text-xs text-zinc-500 mt-1">
            Ajustez les filtres ou élargissez la période pour afficher davantage de résultats.
          </p>
        </div>
      </div>
    </td>
  </tr>
);
